import { Helmet } from 'react-helmet-async'
import { BeakerIcon, OfficeBuildingIcon, ShieldCheckIcon } from '@heroicons/react/outline'

import Hero from '../Hero'
import About from '../About'
import ServiceCard from '../ServiceCard'
import Button from '../ui/Button'
import Container from '../ui/Container'
import SectionHeading from '../ui/SectionHeading'
import { services, accreditations, partners, businessInfo } from '../../data/site'

const accreditationIcons = {
  BeakerIcon,
  OfficeBuildingIcon,
  ShieldCheckIcon,
}

function Home() {
  const featuredServices = services.slice(0, 3)

  return (
    <>
      <Helmet>
        <title>Meditex Healthcare Hub Limited | Diagnostic Care in Lagos & Ogun</title>
        <meta
          name="description"
          content="Meditex Healthcare Hub Limited is a quality-driven medical diagnostic laboratory serving Ikorodu and Ogijo, accredited by MLSCN, CAC and HEFAMAA."
        />
      </Helmet>

      <Hero />

      <div className="bg-white py-16" id="about">
        <Container>
          <About variant="summary" onDark={false} />
        </Container>
      </div>

      <div className="bg-primary-50 py-16" id="accreditations">
        <Container>
          <SectionHeading
            eyebrow="Fully accredited"
            title="Accreditations"
            description={`${businessInfo.name} is fully registered with the Corporate Affairs Commission and the Medical Laboratory Science Council of Nigeria (MLSCN). We are also accredited by the Lagos State Health Facility Monitoring and Accreditation Agency (HEFAMAA) as a secondary healthcare provider.`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {accreditations.map((accred) => {
              const Icon = accreditationIcons[accred.icon] || ShieldCheckIcon
              return (
                <div key={accred.name} className="flex items-center gap-4 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <span className="flex items-center justify-center h-12 w-12 rounded-full bg-accent-50 shrink-0">
                    <Icon className="h-6 w-6 text-accent-600" />
                  </span>
                  <div className="text-left">
                    <p className="font-bold text-primary">{accred.name}</p>
                    <p className="text-gray-500 text-xs">{accred.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </Container>
      </div>

      <div className="bg-white py-16" id="services">
        <Container>
          <SectionHeading
            eyebrow="What we offer"
            title="Our Services"
            description="A wide range of diagnostic services, from routine health checks to specialised pathology."
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {featuredServices.map((service) => (
              <ServiceCard key={service.slug} service={service} shouldTruncate />
            ))}
          </div>
          <div className="flex justify-center">
            <Button to="/services" variant="secondary">View All Services</Button>
          </div>
        </Container>
      </div>

      <div className="bg-primary-50 py-16">
        <Container>
          <SectionHeading eyebrow="Trusted by" title="Our Partner Hospitals & Clinics" />
          <div className="flex flex-wrap justify-center gap-3">
            {partners.map((partner) => (
              <span key={partner} className="bg-white text-primary-700 rounded-full px-4 py-2 text-sm font-medium shadow-sm border border-gray-100">
                {partner}
              </span>
            ))}
          </div>
        </Container>
      </div>

      <div className="bg-gradient-to-br from-primary to-accent-700 py-16">
        <Container className="flex flex-col items-center text-center gap-4">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Ready to book a diagnostic test?</h2>
          <p className="max-w-xl text-primary-100">
            Reach out to our Ikorodu or Ogijo centre, or send us a message and we'll respond promptly.
          </p>
          <Button to="/contact" variant="primary" className="mt-2">Contact Us</Button>
        </Container>
      </div>
    </>
  )
}

export default Home
